import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Machines } from "@/components/settings/machines";
import { T3Code } from "@/components/settings/t3code";
import {
  SettingRow,
  Segmented,
  SettingsSection,
} from "@/components/settings/section";
import {
  THEMES,
  useLanguageChoice,
  useThemeChoice,
} from "@/components/shell/preferences";
import { useWorkspace } from "@/components/shell/workspace-context";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { LANGUAGES } from "@/i18n/languages";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";
import { NAME_MAX, cleanName } from "@/lib/slugs";

export const Route = createFileRoute("/_app/$workspaceSlug/settings/account")({
  component: AccountSettings,
});

function AccountSettings() {
  const { t } = useTranslation("settings");
  const { t: tShell } = useTranslation("shell");
  const [theme, setTheme] = useThemeChoice();
  const [language, setLanguage] = useLanguageChoice();
  const { workspace } = useWorkspace();

  return (
    <>
      <ProfileForm />
      <SettingsSection title={t("account.preferences")}>
        <div className="divide-y border-y">
          <SettingRow label={tShell("theme.label")} description={t("account.themeHint")}>
            <Segmented
              label={tShell("theme.label")}
              value={theme}
              onChange={setTheme}
              options={THEMES.map((value) => ({ value, label: tShell(`theme.${value}`) }))}
            />
          </SettingRow>
          <SettingRow label={tShell("language.label")} description={t("account.languageHint")}>
            <Segmented
              label={tShell("language.label")}
              value={language}
              onChange={setLanguage}
              options={LANGUAGES.map((item) => ({ value: item.code, label: item.endonym }))}
            />
          </SettingRow>
        </div>
      </SettingsSection>
      <Machines workspaceId={workspace._id} />
      <T3Code workspaceId={workspace._id} />
      <SettingsSection title={t("account.session")}>
        <div>
          <Button variant="outline" onClick={() => void authClient.signOut()}>
            {tShell("user.signOut")}
          </Button>
        </div>
      </SettingsSection>
    </>
  );
}

function ProfileForm() {
  const { t } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { t: tCommon } = useTranslation();
  const { me } = useWorkspace();
  const [name, setName] = useState(me.name);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const cleaned = cleanName(name);
  const dirty = cleaned !== me.name;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!cleaned) {
      setError(tErrors("nameInvalid"));
      return;
    }
    setError(null);
    setPending(true);
    const result = await authClient.updateUser({ name: cleaned });
    setPending(false);
    if (result.error) setError(authErrorMessage(result.error, tErrors));
    else toast(t("saved"));
  }

  return (
    <SettingsSection title={t("account.profile")}>
      <form onSubmit={onSubmit} noValidate className="flex max-w-sm flex-col gap-4">
        <Field label={t("account.name")} htmlFor="account-name">
          <Input
            id="account-name"
            autoComplete="name"
            maxLength={NAME_MAX}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label={t("account.email")} htmlFor="account-email" hint={t("account.emailHint")}>
          <Input id="account-email" value={me.email} readOnly disabled />
        </Field>
        <FormError>{error}</FormError>
        <div>
          <Button type="submit" disabled={pending || !dirty}>
            {pending ? <Spinner /> : null}
            {tCommon("actions.save")}
          </Button>
        </div>
      </form>
    </SettingsSection>
  );
}
